import React, { useState } from 'react';
import ReactJson from 'react-json-view';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button } from 'ui';
import { EMAIL_REG_EXP } from 'lib/validations';
import { SIGNUP_FORM_INITIAL_STATE, FormFieldsTypes, ISignUpFields, SignUpInputTypes } from '../model/SignUp.model';
import { signUp } from '../api/SignUp.client';
import { useStyles } from './SignUpForm.styles';

export function SignUpForm() {
    const classes = useStyles();
    const [inputType, setInputType] = useState<string>(SignUpInputTypes.Password);
    const [isPasswordShow, setIsPasswordShow] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<any>>(null);
    const [successResponse, setSuccessResponse] = useState<Nullable<any>>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting, isSubmitted } } = useForm<ISignUpFields>({
        mode: 'onChange',
        defaultValues: SIGNUP_FORM_INITIAL_STATE,
    });

    const signInEmailRegister = register(FormFieldsTypes.Email, {
        required: 'Email is required',
        pattern: {
            value: EMAIL_REG_EXP,
            message: 'Email is required',
        },
        // validate: (v) => isCorrectEmail(v),
    });

    const signInPasswordRegister = register(FormFieldsTypes.Password, {
        required: 'Password is required',
        // pattern: {
        //     value: PASSWORD_REG_EXP,
        //     message: 'Your password should contain an upper and a lower case alphabet, a numeric and a special character',
        // },
        maxLength: {
            value: 30,
            message: 'Maximum 30 characters',
        },
    });

    const handleClickShowPassword = () => {
        setInputType(isPasswordShow ? SignUpInputTypes.Password : SignUpInputTypes.Input);
        setIsPasswordShow(!isPasswordShow);
    };

    const handleFormSubmit: SubmitHandler<ISignUpFields> = async (params) => {
        try {
            const { data } = await signUp(params);
            setSuccessResponse(data);
        } catch (error: any) {
            setErrorMessage({
                response: error?.response || {},
                request: error?.request || {},
            });
        }
    };

    const errorNode = (error) => (
        <>
            <span className={classes.infoIcon}><AiOutlineInfoCircle /></span>
            <span className={classes.errorMessage}>{error}</span>
        </>
    );

    return (
        <Box
            sx={{
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
                Sign up
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(handleFormSubmit)} sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            {...signInEmailRegister}
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            helperText={isSubmitted && !!errors[FormFieldsTypes.Email] && errorNode(errors?.[FormFieldsTypes.Email]?.message)}
                            error={isSubmitted && !!errors[FormFieldsTypes.Email]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            {...signInPasswordRegister}
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            id="password"
                            autoComplete="new-password"
                            helperText={isSubmitted && errors?.[FormFieldsTypes.Password]?.message && errorNode(errors?.[FormFieldsTypes.Password]?.message)}
                            error={isSubmitted && !!errors[FormFieldsTypes.Password]}
                            type={inputType}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment className={classes.visibilityButton} position="end" onClick={handleClickShowPassword}>
                                        {isPasswordShow ? <Visibility /> : <VisibilityOff />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    isLoading={isSubmitting}
                >
                    Sign Up
                </Button>
                <Grid container justifyContent="flex-end">
                    <Grid item>
                        <Link to="/">
                            Already have an account? Sign in
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {successResponse && (
                <ReactJson src={successResponse} collapsed={1} theme="monokai" />
            )}

            {errorMessage && (
                <ReactJson src={errorMessage} collapsed={1} theme="monokai" style={{ maxWidth: 500 }} />
            )}
        </Box>
    );
}
